import { DEFAULT_LOCALE, isLocale, LOCALES, type Locale } from '@pickpro/i18n';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';
import { readPreferences, writePreferences } from '../shared/storage/preferences';
import { dropdownItemClass } from '../shared/ui/classes';
import { Dropdown } from '../shared/ui/dropdown';
import { swapLocaleSegment } from './locale-path';

const nativeNames: Record<Locale, string> = {
  en: 'English',
  'pt-BR': 'Português',
  es: 'Español',
};

const localeCodes: Record<Locale, string> = {
  en: 'EN',
  'pt-BR': 'PT',
  es: 'ES',
};

const GlobeIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="10" cy="10" r="7.25" />
    <ellipse cx="10" cy="10" rx="3.25" ry="7.25" />
    <path d="M3.1 7.5h13.8M3.1 12.5h13.8" strokeLinecap="round" />
  </svg>
);

const CheckIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m3.5 8.5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const abovePanelClass =
  'absolute bottom-full left-0 z-10 mb-2.5 w-56 origin-bottom-left rounded-md bg-surface py-2 shadow-lg shadow-shadow/15 ring-1 ring-line focus:outline-hidden';

export const LocaleSwitcher = ({
  placement = 'below',
  showName = false,
}: {
  placement?: 'below' | 'above';
  showName?: boolean;
}) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { locale } = useParams();
  const current = isLocale(locale) ? locale : DEFAULT_LOCALE;

  return (
    <Dropdown
      label={t('nav.language')}
      haspopup="menu"
      autoFocusItem
      triggerClassName={`flex items-center gap-x-1.5 rounded-md px-2 py-1.5 text-sm font-semibold hover:bg-raised ${
        showName ? 'text-muted hover:text-content' : 'text-content'
      }`}
      panelClassName={placement === 'above' ? abovePanelClass : undefined}
      trigger={
        <>
          <GlobeIcon className="size-5 text-muted" />
          <span>{showName ? nativeNames[current] : localeCodes[current]}</span>
        </>
      }
    >
      {(close) =>
        LOCALES.map((option) => (
          <li key={option}>
            <Link
              to={`${swapLocaleSegment(pathname, option)}${search}`}
              className={`${dropdownItemClass} flex items-center justify-between`}
              aria-current={option === current ? 'true' : undefined}
              onClick={() => {
                writePreferences({ ...readPreferences(), locale: option });
                close();
              }}
            >
              <span>{nativeNames[option]}</span>
              {option === current ? <CheckIcon className="size-4 text-accent-ink" /> : null}
            </Link>
          </li>
        ))
      }
    </Dropdown>
  );
};
